import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

/**
 * Handles Supabase auth callbacks:
 * - Email confirmation: /auth/confirm?token_hash=...&type=signup
 * - Password reset: /auth/confirm?token_hash=...&type=recovery
 * - Magic link: /auth/confirm?token_hash=...&type=magiclink
 *
 * Also handles the hash-based flow where Supabase redirects to
 * /#access_token=...&type=signup (picked up by AuthContext automatically).
 */
export default function AuthCallbackPage() {
  const navigate = useNavigate()
  const [error, setError] = useState('')
  const [verifying, setVerifying] = useState(true)

  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-theme')
    document.documentElement.setAttribute('data-theme', 'dark')
    return () => { if (prev) document.documentElement.setAttribute('data-theme', prev); else document.documentElement.removeAttribute('data-theme') }
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const tokenHash = params.get('token_hash')
    const type = params.get('type') as 'signup' | 'recovery' | 'magiclink' | 'email' | null

    if (tokenHash && type) {
      // PKCE flow — verify the token
      supabase.auth.verifyOtp({ token_hash: tokenHash, type: type === 'signup' ? 'email' : type })
        .then(({ error: err }) => {
          if (err) {
            console.error('[Auth Callback] verifyOtp error:', err.message)
            setError(err.message)
          } else {
            console.log('[Auth Callback] Verified successfully')
            // Check if there's a pending invite
            const pendingInvite = sessionStorage.getItem('skilflow_pending_invite')
            if (pendingInvite) {
              if (pendingInvite.startsWith('join:')) {
                navigate(`/join/${pendingInvite.slice(5)}`, { replace: true })
              } else if (pendingInvite.startsWith('org:')) {
                navigate(`/invitations/accept?token=${pendingInvite.slice(4)}`, { replace: true })
              } else {
                navigate(`/invite/${pendingInvite}`, { replace: true })
              }
            } else {
              navigate('/', { replace: true })
            }
          }
        })
        .finally(() => setVerifying(false))
    } else {
      // Hash-based flow — Supabase JS client handles this automatically
      // via onAuthStateChange. Just redirect to dashboard after a short wait.
      setTimeout(() => {
        const pendingInvite = sessionStorage.getItem('skilflow_pending_invite')
        if (pendingInvite) {
          if (pendingInvite.startsWith('join:')) {
            navigate(`/join/${pendingInvite.slice(5)}`, { replace: true })
          } else if (pendingInvite.startsWith('org:')) {
            navigate(`/invitations/accept?token=${pendingInvite.slice(4)}`, { replace: true })
          } else {
            navigate(`/invite/${pendingInvite}`, { replace: true })
          }
        } else {
          navigate('/', { replace: true })
        }
      }, 1500)
      setVerifying(false)
    }
  }, [navigate])

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ width: '100%', maxWidth: 420, textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 32 }}>
            <svg width="28" height="28" viewBox="0 0 28 28"><rect width="28" height="28" rx="6" fill="#7c3aed"/><rect x="4" y="7" width="20" height="4.5" rx="2.25" fill="white" transform="rotate(-10,14,9.25)"/><rect x="4" y="14" width="14" height="4.5" rx="2.25" fill="white" opacity=".55" transform="rotate(-10,11,16.25)"/><rect x="4" y="21" width="8" height="4.5" rx="2.25" fill="white" opacity=".22" transform="rotate(-10,8,23.25)"/></svg>
            <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 17, fontWeight: 700, color: 'var(--text1)' }}>Skil<span style={{ color: 'var(--brand-light)' }}>flow</span></span>
          </div>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: '40px 32px' }}>
            <div style={{ fontSize: 36, marginBottom: 16 }}>&#9888;</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text1)', marginBottom: 8 }}>Confirmation failed</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 24, lineHeight: 1.5 }}>{error}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button onClick={() => navigate('/signup')} className="btn-primary" style={{ padding: '10px 24px', fontSize: 14, width: '100%' }}>
                Request new confirmation
              </button>
              <button onClick={() => navigate('/login')} className="btn-ghost" style={{ padding: '8px 24px', fontSize: 13, width: '100%' }}>
                Go to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
          <svg width="28" height="28" viewBox="0 0 28 28"><rect width="28" height="28" rx="6" fill="#7c3aed"/><rect x="4" y="7" width="20" height="4.5" rx="2.25" fill="white" transform="rotate(-10,14,9.25)"/><rect x="4" y="14" width="14" height="4.5" rx="2.25" fill="white" opacity=".55" transform="rotate(-10,11,16.25)"/><rect x="4" y="21" width="8" height="4.5" rx="2.25" fill="white" opacity=".22" transform="rotate(-10,8,23.25)"/></svg>
          <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 17, fontWeight: 700, color: 'var(--text1)' }}>Skil<span style={{ color: 'var(--brand-light)' }}>flow</span></span>
        </div>
        <div style={{ color: 'var(--text2)', fontSize: 14 }}>
          {verifying ? 'Confirming your account...' : 'Redirecting...'}
        </div>
      </div>
    </div>
  )
}
